<template>
    <div class="c-sds col-xl-4 col-lg-4 col-md-4">
        <!-- ===== Service details card ===== -->
        <CCard :extra-style="cardStyle">
            <h3
                class="c-sds__title"
                :data-sale-id="saleLineItem.hasOwnProperty('id')
                    ? saleLineItem.id
                    : saleLineItem.deal_line_item.id"
                :style="titleStyle"
            >Service details</h3>

            <div :style="kvStackStyle">
                <CKv
                    v-if="saleLineItem.service && saleLineItem.service.label"
                    :k="'Service'"
                    :v="saleLineItem.service.label || '-'"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.custom_service"
                    :k="'Custom service'"
                    :v="saleLineItem.deal_line_item.custom_service || '-'"
                />
                <CKv
                    :k="'Type'"
                    :v="getTypeName(saleLineItem.deal_line_item.type_id) || '-'"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.kickoff_date"
                    :k="'Kick-off date'"
                    :v="kickOffDateFormat(saleLineItem.deal_line_item.kickoff_date)"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 1
                        && saleLineItem.deal_line_item.amount
                        && !saleLineItem.deal_line_item.fluctuating_fee"
                    :k="'Monthly fee'"
                    :v="'$' + (numberWithCommas(saleLineItem.deal_line_item.amount) || '-')"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 1
                        && saleLineItem.deal_line_item.fluctuating_fee"
                    :k="'Fluctuating fee'"
                    :v="saleLineItem.deal_line_item.fluctuating_fee ? 'Yes' : 'No'"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 1
                        && saleLineItem.deal_line_item.frequency_retainer"
                    :k="'Frequency'"
                    :v="Number(saleLineItem.deal_line_item.frequency_retainer) === 1 ? '1m' : '1y'"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 2
                        && saleLineItem.deal_line_item.amount"
                    :k="'Full amount'"
                    :v="'$' + (numberWithCommas(saleLineItem.deal_line_item.amount) || '-')"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 2
                        && saleLineItem.deal_line_item.num_of_payments"
                    :k="'Number of pmts'"
                    :v="numOfPmtsLabel"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 2
                        && saleLineItem.deal_line_item.num_of_payments"
                    :k="'Total summary'"
                    :v="saleLineItem.deal_line_item.num_of_payments || '-'"
                />
                <CKv
                    v-if="saleLineItem.full_amount_later"
                    :k="'Reason'"
                    :v="'Full amount will be paid later'"
                />
                <CKv
                    v-else-if="saleLineItem.is_dissapproved"
                    :k="'Reason'"
                    :v="'Is dissapproved'"
                />
                <CKv
                    v-if="saleLineItem.deal_line_item.type_id === 3
                        && saleLineItem.deal_line_item.amount"
                    :k="'One-time fee'"
                    :v="'$' + (numberWithCommas(saleLineItem.deal_line_item.amount) || '-')"
                />
            </div>

            <!-- Outstanding balance highlight callout — kept its red-on-red
                 treatment because the original sidebar was the only place a
                 non-zero balance was surfaced before the new commission card. -->
            <div
                v-if="hasOutstanding"
                :style="outstandingCalloutStyle"
            >
                <div :style="outstandingLabelStyle">Outstanding balance</div>
                <div :style="outstandingValueStyle">
                    ${{ numberWithCommas(saleLineItem.cumulative_difference) || '-' }}
                </div>
            </div>
        </CCard>

        <!-- ===== Sale history card ===== -->
        <CCard :extra-style="cardStyleTight">
            <div :style="historyHeaderStyle">
                <h3 :style="titleStyle">Sale history</h3>
                <span v-if="hasHistory" :style="historyMetaStyle">
                    last {{ saleLineItem.history.length }} period{{ saleLineItem.history.length === 1 ? '' : 's' }}
                </span>
            </div>

            <!-- Sparkline preview when we have at least 2 collected points -->
            <div v-if="sparklineHistory.length > 1" :style="sparklineWrapStyle">
                <Sparkline :history="sparklineHistory" :color="'#1F1B17'" :height="40" />
            </div>

            <!-- Compact c-design history table — keeps <table><tbody><tr> DOM
                 so the existing test contract for sale-history rows is preserved. -->
            <div :style="tableWrapStyle">
                <table class="c-sds__history" :style="tableStyle">
                    <thead>
                        <tr :style="trHeadStyle">
                            <th :style="thStyle">Period</th>
                            <th :style="thStyleRight">Invoiced</th>
                            <th :style="thStyleRight">Collected</th>
                            <th :style="thStyleRight">Commission</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="hasHistory">
                            <tr
                                v-for="(item, i) in saleLineItem.history"
                                :key="`hist-${item.period}-${i}`"
                                :style="trBodyStyle(i)"
                            >
                                <td :style="tdStyle">{{ item.period }}</td>
                                <td :style="tdStyleNum">${{ item.amount }}</td>
                                <td :style="tdStyleNum">${{ item.collected }}</td>
                                <td :style="tdStyleNum">${{ item.commission_owed }}</td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr>
                                <td :style="tdEmptyStyle" colspan="4">No data</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </CCard>
    </div>
</template>

<script>
import CCard from "../c-design/CCard.vue";
import CKv from "../c-design/CKv.vue";
import Sparkline from "../c-design/Sparkline.vue";
import {
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    TAG_RED_DEEP,
    FONT_DISPLAY,
} from "../c-design/tokens";

/**
 * Variant-C visual port (Stage 5.5 of wizard redesign). Same props and
 * the same data shape as before — only the template / styling layer is
 * new. Two CCard panels:
 *
 *  1. Service details — CKv rows for each conditionally-rendered fact
 *     about the deal line item; an Outstanding-balance callout block
 *     when cumulative_difference is non-null (keeps its red tone)
 *  2. Sale history — a Sparkline preview (when 2+ history points) +
 *     a compact <table> with one row per saleLineItem.history entry.
 *     The <table><tbody><tr> structure is preserved verbatim so the
 *     existing Vitest cases for sale-history rendering keep passing.
 *
 * Sits in the parent's wizard_sale_wrap.row Bootstrap layout as a
 * col-xl-4 alongside whichever main pane is currently rendered
 * (MainQuestionsScreen / CollectedInformationScreen / etc.).
 */
export default {
    name: "ServiceDetailsSidebar",
    components: { CCard, CKv, Sparkline },
    props: {
        saleLineItem: {
            type: Object,
            required: true,
        },
        getTypeName: {
            type: Function,
            required: true,
        },
        numberWithCommas: {
            type: Function,
            required: true,
        },
        kickOffDateFormat: {
            type: Function,
            required: true,
        },
    },
    computed: {
        numOfPmtsLabel() {
            const dli = this.saleLineItem.deal_line_item;
            if (dli.num_of_payments || dli.current_number_payment) {
                return `${dli.current_number_payment || '-'} (${dli.num_of_payments})`;
            }
            return '-';
        },
        hasOutstanding() {
            const v = this.saleLineItem.cumulative_difference;
            return v !== null && v !== undefined;
        },
        hasHistory() {
            return Array.isArray(this.saleLineItem.history) && this.saleLineItem.history.length > 0;
        },
        sparklineHistory() {
            if (!this.hasHistory) return [];
            // Sparkline reads `col` (collected per period). Map the
            // sidebar's history shape into that contract.
            return this.saleLineItem.history.map((h) => ({ col: Number(h.collected) || 0 }));
        },
        // ===== Style maps =====
        cardStyle() {
            return { marginBottom: '18px', padding: '24px 24px' };
        },
        cardStyleTight() {
            return { marginBottom: '18px', padding: '20px 20px' };
        },
        titleStyle() {
            return {
                margin: 0,
                fontFamily: FONT_DISPLAY,
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: C_MUTED,
            };
        },
        kvStackStyle() {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginTop: '14px',
                fontSize: '13px',
            };
        },
        outstandingCalloutStyle() {
            return {
                marginTop: '16px',
                padding: '12px 14px',
                background: '#FFF6F6',
                border: '1px solid #F5DEDA',
                borderRadius: '12px',
            };
        },
        outstandingLabelStyle() {
            return {
                fontSize: '10px',
                color: TAG_RED_DEEP,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                fontWeight: 700,
            };
        },
        outstandingValueStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '20px',
                fontWeight: 700,
                color: TAG_RED_DEEP,
                marginTop: '2px',
            };
        },
        historyHeaderStyle() {
            return {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: '12px',
            };
        },
        historyMetaStyle() {
            return {
                fontSize: '11px',
                color: C_MUTED,
            };
        },
        sparklineWrapStyle() {
            return {
                padding: '6px',
                background: C_CREAM,
                borderRadius: '8px',
                marginBottom: '12px',
            };
        },
        tableWrapStyle() {
            return {
                maxHeight: '320px',
                overflowY: 'auto',
                border: `1px solid ${C_HAIR}`,
                borderRadius: '10px',
            };
        },
        tableStyle() {
            return {
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px',
            };
        },
        trHeadStyle() {
            return {
                background: C_INK,
                color: '#fff',
                position: 'sticky',
                top: 0,
            };
        },
        thStyle() {
            return {
                padding: '8px 10px',
                textAlign: 'left',
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
            };
        },
        thStyleRight() {
            return { ...this.thStyle, textAlign: 'right' };
        },
        tdStyle() {
            return {
                padding: '8px 10px',
                fontSize: '12px',
                color: C_INK2,
                fontFamily: FONT_DISPLAY,
                fontWeight: 600,
            };
        },
        tdStyleNum() {
            return {
                ...this.tdStyle,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
            };
        },
        tdEmptyStyle() {
            return {
                padding: '20px',
                textAlign: 'center',
                color: C_MUTED,
                fontSize: '13px',
            };
        },
    },
    methods: {
        trBodyStyle(i) {
            return {
                background: i % 2 === 0 ? '#fff' : C_CREAM,
                borderTop: `1px solid ${C_HAIR}`,
            };
        },
    },
};
</script>
